'use client';

import { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import RoleSwitcherDropdown from '@/components/common/RoleSwitcherDropdown';
import NotificationBell from '@/components/common/NotificationBell';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'warning' | 'success' | 'error' | 'info';
  time: string;
  read: boolean;
}

const mockNotifications: Notification[] = [
  { id: '1', title: 'Weather Alert', message: 'Storm warning for Job #1042 - Oak Street', type: 'warning', time: '5m ago', read: false },
  { id: '2', title: 'Material Delivered', message: 'Shingles delivered to Maple Ave site', type: 'success', time: '22m ago', read: false },
  { id: '3', title: 'Crew Update', message: 'Team B checked in at Riverside project', type: 'info', time: '1h ago', read: true },
  { id: '4', title: 'Inspection Required', message: 'Quality check needed at Pine St job', type: 'error', time: '2h ago', read: true },
];

interface UtilityHeaderProps {
  onMenuToggle?: () => void;
  isSidebarOpen?: boolean;
}

const UtilityHeader = ({ onMenuToggle, isSidebarOpen = false }: UtilityHeaderProps) => {
  const { currentUser, signOut } = useAuth();
  const router = useRouter();
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showDialer, setShowDialer] = useState(false);
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [dialerInput, setDialerInput] = useState('');
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);

  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const dialerRef = useRef<HTMLDivElement>(null);
  const roleSwitcherRef = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Derive initials from current user
  const userInitials = `${currentUser.first_name.charAt(0)}${currentUser.last_name.charAt(0)}`;
  const displayName = `${currentUser.first_name} ${currentUser.last_name}`;
  const shortName = `${currentUser.first_name} ${currentUser.last_name.charAt(0)}.`;

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleDialerKey = (key: string) => {
    setDialerInput(prev => prev + key);
  };

  const clearDialer = () => setDialerInput('');

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setShowProfile(false);
      if (dialerRef.current && !dialerRef.current.contains(e.target as Node)) setShowDialer(false);
      if (roleSwitcherRef.current && !roleSwitcherRef.current.contains(e.target as Node)) setShowRoleSwitcher(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const notifIconColor: Record<string, string> = {
    warning: 'text-warning',
    success: 'text-success',
    error: 'text-error',
    info: 'text-primary',
  };

  const dialerKeys = ['1','2','3','4','5','6','7','8','9','*','0','#'];

  return (
    <header
      className="fixed top-0 left-0 right-0 h-[60px] bg-card border-b border-border z-header flex items-center"
      style={{ paddingLeft: '250px' }}
    >
      {/* Mobile: left section with hamburger */}
      <div className="flex items-center h-full lg:hidden pl-4">
        <button
          onClick={onMenuToggle}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-standard min-w-touch min-h-touch flex items-center justify-center"
          aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isSidebarOpen}
        >
          <Icon name={isSidebarOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
        </button>
      </div>

      {/* Main header content */}
      <div className="flex items-center justify-between w-full px-5 h-full">
        {/* Left spacer (Clock In feature removed) */}
        <div />

        {/* Right: Utility icons */}
        <div className="flex items-center gap-1">
          {/* VoIP Dialer */}
          <div className="relative" ref={dialerRef}>
            <button
              onClick={() => { setShowDialer(p => !p); setShowNotifications(false); setShowProfile(false); }}
              className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-standard min-w-touch min-h-touch flex items-center justify-center"
              aria-label="Open VoIP dialer"
              aria-expanded={showDialer}
              suppressHydrationWarning
            >
              <Icon name="PhoneIcon" size={20} variant="outline" />
            </button>
            {showDialer && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-md shadow-xl z-50 p-4">
                <p className="font-heading text-sm font-semibold text-foreground mb-3">VoIP Dialer</p>
                <div className="bg-muted rounded-md px-3 py-2 mb-3 min-h-[40px] flex items-center justify-between">
                  <span className="font-data text-base text-foreground tracking-widest">{dialerInput || <span className="text-muted-foreground text-sm font-body">Enter number</span>}</span>
                  {dialerInput && (
                    <button onClick={() => setDialerInput(p => p.slice(0, -1))} className="text-muted-foreground hover:text-foreground transition-standard">
                      <Icon name="BackspaceIcon" size={16} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {dialerKeys.map(key => (
                    <button
                      key={key}
                      onClick={() => handleDialerKey(key)}
                      className="h-10 rounded-md bg-muted hover:bg-border text-foreground font-data text-sm font-medium transition-standard press-scale"
                    >
                      {key}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    className="flex-1 h-10 bg-success text-success-foreground rounded-md font-body text-sm font-medium flex items-center justify-center gap-2 transition-standard hover-lift press-scale"
                    onClick={() => { /* initiate call */ }}
                  >
                    <Icon name="PhoneIcon" size={16} variant="solid" />
                    Call
                  </button>
                  <button
                    onClick={clearDialer}
                    className="h-10 px-3 bg-muted text-muted-foreground rounded-md font-body text-sm transition-standard hover:bg-border"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Messenger */}
          <button
            className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-standard min-w-touch min-h-touch flex items-center justify-center"
            aria-label="Open messenger"
            suppressHydrationWarning
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} variant="outline" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" aria-hidden="true" />
          </button>

          {/* Notifications Bell */}
          <NotificationBell />

          {/* Role Switcher — SuperAdmin only */}
          {currentUser?.roles.includes('Admin') && (
            <div className="relative mx-1" ref={roleSwitcherRef}>
              {isImpersonating && impersonatedUser ? (
                <button
                  onClick={() => setShowRoleSwitcher(p => !p)}
                  className="flex items-center gap-1.5 px-3 h-8 rounded-full border-2 font-body text-xs font-semibold transition-standard press-scale"
                  style={{ borderColor: '#F59E0B', color: '#F59E0B', background: 'rgba(245,158,11,0.08)' }}
                  aria-label="Active preview mode"
                  suppressHydrationWarning
                >
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                  <span className="hidden sm:inline truncate max-w-[120px]">
                    {impersonatedUser.first_name} · {impersonatedUser.role.replace(/_/g, ' ')}
                  </span>
                  <Icon name="ChevronDownIcon" size={12} />
                </button>
              ) : (
                <button
                  onClick={() => { setShowRoleSwitcher(p => !p); setShowNotifications(false); setShowProfile(false); setShowDialer(false); }}
                  className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-muted border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 font-body text-xs font-medium transition-standard press-scale"
                  aria-label="Role switcher"
                  aria-expanded={showRoleSwitcher}
                  suppressHydrationWarning
                >
                  <Icon name="UsersIcon" size={14} variant="outline" />
                  <span className="hidden sm:inline">View As: All Roles</span>
                  <Icon name="ChevronDownIcon" size={12} />
                </button>
              )}
              {showRoleSwitcher && (
                <RoleSwitcherDropdown onClose={() => setShowRoleSwitcher(false)} />
              )}
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={async () => {
              await signOut();
              router.replace('/login-authentication-screen');
            }}
            className="flex items-center gap-1.5 px-3 h-9 rounded-md text-muted-foreground hover:text-error hover:bg-error/5 transition-standard press-scale font-body text-sm font-medium"
            aria-label="Sign out"
            title="Sign out"
            suppressHydrationWarning
          >
            <Icon name="ArrowRightOnRectangleIcon" size={18} variant="outline" />
            <span className="hidden lg:inline">Logout</span>
          </button>

          {/* Profile — dynamically shows current user */}
          <div className="relative ml-1" ref={profileRef}>
            <button
              onClick={() => { setShowProfile(p => !p); setShowNotifications(false); setShowDialer(false); }}
              className="flex items-center gap-2 pl-2 pr-3 h-9 rounded-md hover:bg-muted transition-standard press-scale"
              aria-label="User profile menu"
              aria-expanded={showProfile}
              suppressHydrationWarning
            >
              {currentUser?.profile_image ? (
                <img
                  src={currentUser.profile_image}
                  alt={`${displayName} profile`}
                  className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <span className="font-heading text-xs font-semibold text-white">{userInitials}</span>
                </div>
              )}
              <span className="font-body text-sm font-medium text-foreground hidden md:inline">{shortName}</span>
              <Icon name="ChevronDownIcon" size={14} className="text-muted-foreground hidden md:block" />
            </button>
            {showProfile && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-md shadow-xl z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-border">
                  <p className="font-body text-sm font-semibold text-foreground">{displayName}</p>
                  <p className="font-caption text-xs text-muted-foreground">{currentUser.email}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {currentUser.roles.map((r) => (
                      <span key={r} className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-caption font-medium bg-primary/10 text-primary">
                        {r.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                  <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-caption font-medium bg-muted text-muted-foreground">
                    ID: {currentUser.id}
                  </span>
                </div>
                <ul className="py-1">
                  {[
                    { label: 'My Profile', icon: 'UserCircleIcon', path: '/me' },
                    { label: 'Settings', icon: 'Cog6ToothIcon', path: null },
                    { label: 'Help & Support', icon: 'QuestionMarkCircleIcon', path: null },
                  ].map(item => (
                    <li key={item.label}>
                      <button
                        onClick={() => {
                          setShowProfile(false);
                          if (item.path) router.push(item.path);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 font-body text-sm text-foreground hover:bg-muted transition-standard text-left">
                        <Icon name={item.icon as any} size={16} className="text-muted-foreground" variant="outline" />
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-border py-1">
                  <button
                    onClick={async () => {
                      await signOut();
                      router.replace('/login-authentication-screen');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 font-body text-sm text-error hover:bg-error/5 transition-standard text-left">
                    <Icon name="ArrowRightOnRectangleIcon" size={16} className="text-error" variant="outline" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default UtilityHeader;